import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const SESSION_STORAGE_KEY = "truth_plus_source";

/**
 * Detects if the user entered from a Truth+ (TV) source based on URL params.
 * @param pathname - The current pathname
 * @param search - The current search string
 * @returns true if the source is T+
 */
export function detectTvSourceFromUrl(
  pathname: string,
  search: string,
): boolean {
  const searchParams = new URLSearchParams(search);
  const tvParam = searchParams.get("tv");
  const idParam = searchParams.get("id");
  const normalizedPathname = pathname.replace(/^\/|\/$/g, "");

  // Check if coming from tv-signup or tv-reset-password path
  return (
    (normalizedPathname === "tv-signup" && !!idParam) ||
    normalizedPathname === "tv-reset-password" ||
    tvParam === "true"
  );
}

/**
 * Reads T+ source status from sessionStorage.
 */
export function readTvSourceFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";
}

/**
 * Detects T+ source from current window location.
 */
export function detectTvSourceFromWindow(): boolean {
  if (typeof window === "undefined") return false;

  // Check sessionStorage first
  if (readTvSourceFromStorage()) return true;

  // Check URL params
  return detectTvSourceFromUrl(
    window.location.pathname,
    window.location.search,
  );
}

/**
 * Detects if the user entered from a Truth+ (TV) source.
 * Checks URL params and persists to sessionStorage for the session.
 */
export function useTvSource(): boolean {
  const location = useLocation();

  const [isTvSource, setIsTvSource] = useState<boolean>(() => {
    // Check sessionStorage first
    const stored = readTvSourceFromStorage();
    if (stored) return true;

    // Check URL params
    return detectTvSourceFromUrl(location.pathname, location.search);
  });

  // Re-check URL params on location change and persist to sessionStorage
  useEffect(() => {
    const stored = readTvSourceFromStorage();
    if (stored) {
      // If already stored, keep it true
      if (!isTvSource) {
        setIsTvSource(true);
      }
      return;
    }

    // Check URL params
    const detected = detectTvSourceFromUrl(location.pathname, location.search);

    if (detected !== isTvSource) {
      setIsTvSource(detected);
    }

    // Persist to sessionStorage if detected
    if (detected && typeof window !== "undefined") {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
    }
  }, [location.pathname, location.search, isTvSource]);

  return isTvSource;
}
